import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
// import GetTheApp from 'screens/GetTheApp';

import { useNavigate } from 'react-router-dom';
import CallToActionModal from 'screens/Profile/components/CallToActionModal';

// Components
import { Button, Clickable, Col, Icon, Row, Spinner2, Text } from 'components';
import { Caption, Comment, Footer, Header, Images, WebsiteView, Youtube } from './components';
import { buildCommentThreads } from './lib/buildCommentThreads';

// Utils
import {
  getCalculatedText,
  isDeviceIOS,
  updateViewsCount as updateViewsCountUtil,
  useOutsideClick
} from 'core';
import { Api, theme } from 'lib';
import mime from 'mime';

const Post = ({
  item,
  handleClose = () => { },
  handleBack,
  showBack,
  handleNext,
  showNext,
  fromNavigation = false,
  hasComments = true,
  isFixed = false,
  isSharedPost = false,
  onHandleUpdateProfileViewsCount
}) => {
  const postRef = useRef(null),
    [comments, setComments] = useState(null),
    [showImage, setShowImage] = useState(true),
    post = useSelector((state) => state.posts).find((x) => x?._id === item?._id),
    user = useSelector((state) => state.user),
    { isMobile } = useSelector((state) => state.appState),
    dispatch = useDispatch(),
    { t } = useTranslation(),
    owner = (post?.owner?._id || post?.owner) === user?._id ? user : post?.owner;
  const isLoggedIn = !!user;
  const navigate = useNavigate();

  const [currentPostViewsCount, setCurrentPostViewsCount] = useState(0);
  const isPostReacted = post
    ? Object.values(post.reactionCounts ?? {})?.some((value) => value > 0) || post.reactions?.length > 0
    : false;

  const [isAPILoading, setIsAPILoading] = useState(true);
  const threads = useMemo(() => buildCommentThreads(comments || []), [comments]);

  // CallToAction Modal States
  const [isCTAModalVisible, setIsCTAModalVisible] = useState(false);
  const isFollowing = user?.following?.includes(owner?._id) ?? false;

  const handleFollowClick = () => {
    setIsCTAModalVisible(true);
  };

  useOutsideClick(postRef, handleClose, ['reactionsContainer', 'mediaNavigator', 'arrows']);

  useEffect(() => {
    setComments([]);
    getPost();
  }, [item?._id]);

  const handleViewCountTracking = async (postDetails) => {
    if (!postDetails || !postDetails._id) {
      return;
    }

    const isLink = !!postDetails.youtubeId || !!postDetails.metaData?.url;
    const isVideo = mime.getType(postDetails.media[0]?.uri)?.includes('video');
    const trigger = isLink ? 'link' : isVideo ? 'video' : 'photo';

    // Fire API request AFTER modal is visible
    const payload = {
      trigger,
      postId: postDetails._id,
      ownerId: postDetails.owner?._id
    };

    if (postDetails.owner?._id !== user?._id) {
      setTimeout(async () => await updateViewCount(payload), 0);

      if (isVideo) {
        // Omit postId intentionally — update only the profile view
        const videoPayload = {
          trigger: 'video',
          userId: postDetails.owner?._id
        };

        await updateViewsCountUtil({
          payload: videoPayload,
          currentUserId: user?._id
        });
      }
    } else {
      setCurrentPostViewsCount(postDetails.viewsCount || 0); // set default to 0
    }
  };

  const updateViewCount = async ({ trigger, postId, ownerId }) => {
    // prevent updating of profile views for the same owner
    if (ownerId === user?._id) {
      return;
    }

    try {
      setIsAPILoading(true);

      const payload = {
        userId: ownerId,
        trigger,
        postId
      };

      // update profile views
      const res = await updateViewsCountUtil({ payload, currentUserId: user?._id });
      if (!res) return;

      const newPostCount = res.post?.newCount;
      setCurrentPostViewsCount(newPostCount || 0); // set default to 0

      if (
        onHandleUpdateProfileViewsCount &&
        typeof onHandleUpdateProfileViewsCount === 'function'
      ) {
        onHandleUpdateProfileViewsCount(); // update post owner current views when commenting
      }
    } catch (err) {
      const message = err.response?.data
        ? err.response.data
        : 'Something went wrong.\nPlease try again later.';

      dispatch({
        type: 'HANDLE_RES',
        payload: { err: message }
      });
    } finally {
      setIsAPILoading(false);
    }
  };

  async function getPost() {
    let postDetails = post;

    try {
      const res = await Api.post(`/posts/getById`, { postId: item?._id });
      if (!res || !res.post) {
        dispatch({
          type: 'HANDLE_RES',
          payload: { err: 'This content is not available.' }
        });
        return;
      }

      postDetails = res.post;

      // Create caption read more preview
      res.post.calculatedText = await getCalculatedText(res.post?.caption);

      dispatch({
        type: 'ADD_POSTS',
        payload: [res.post]
      });

      const comments = await Promise.all(
        res.comments.map(async (x) => ({
          ...x,
          calculatedText: await getCalculatedText(x.body, {
            width: 355,
            maxLength: 860,
            numberOfLines: 3,
            // HACK: We put an extra letter here to compensate for the different
            // styling of the name on comments
            addToStart: x.owner.name + 'm '
          })
        }))
      );

      await handleViewCountTracking(res.post);

      setComments(comments);
    } catch (err) {
      const message = err.response?.data
        ? err.response.data
        : 'Something went wrong.\nPlease try again later.';

      dispatch({
        type: 'HANDLE_RES',
        payload: { err: message }
      });
    } finally {
      // set api loading state here to false since the other function which set it to false won't be called
      if (postDetails?.owner?._id === user?._id) {
        setIsAPILoading(false);
      }
    }
  }

  function handleNextPost() {
    setIsAPILoading(true);
    setShowImage(false);
    handleNext();

    setTimeout(() => setShowImage(true), 100);
  }

  function handleBackPost() {
    setIsAPILoading(true);
    setShowImage(false);
    handleBack();

    setTimeout(() => setShowImage(true), 100);
  }

  // if(isMobile) return <GetTheApp alwaysShow />
  return (
    <>
      <Col
        pos={isFixed ? 'fixed' : isDeviceIOS() ? '' : 'fixed'}
        left="0"
        top="0"
        zIndex={fromNavigation ? 'unset' : '3'}
        wid="100%"
        // ht={fromNavigation ? 'calc(100vh - 72px)' : '80vh'}
        ht={'100vh'}
        marg={!isMobile && fromNavigation && isLoggedIn ? '2rem 0 0 0' : 0}
        bg="rgba(47, 47, 47, 0.8)"
      >
        {!fromNavigation && (
          <Clickable
            onClick={handleClose}
            wid={isMobile ? '90vw !important' : '936px !important'}
            ht={isDeviceIOS() ? 0 : '40px'}
            marg={
              isMobile && !isDeviceIOS()
                ? '-32px auto 12px auto'
                : isMobile
                  ? '0'
                  : '0 auto 12px auto'
            }
            zIndex={9999}
            style={{
              position: 'relative'
            }}
          >
            <Row
              pos={'absolute'}
              // pos={isDeviceIOS() ? 'absolute' : 'fixed'}
              right={isDeviceIOS() ? '14px' : '0'}
              top={isDeviceIOS() ? '14px' : '0'}
              pad="10px"
              hasRadius="32px"
              hasBorder="2px solid #fff"
            >
              <Icon type="close" col="#fff" dimensions={14} />
            </Row>
          </Clickable>
        )}

        {showBack && (
          <Clickable
            onClick={handleBackPost}
            goThrough
            className="arrows"
            style={{ zIndex: '9999' }}
          >
            <Row
              pos="fixed"
              left="24px"
              top="calc(100vh/2 - 22px)"
              pad="10px"
              hasRadius="32px"
              bg={isMobile ? '#000' : 'transparent'}
              hasBorder="2px solid #fff"
            >
              <Icon type="arrowLeft" col="#fff" dimensions={14} />
            </Row>
          </Clickable>
        )}

        {showNext && (
          <Clickable
            onClick={handleNextPost}
            goThrough
            className="arrows"
            style={{ zIndex: '9999' }}
          >
            <Row
              pos="fixed"
              right="24px"
              top="calc(100vh/2 - 22px)"
              pad="10px"
              hasRadius="32px"
              bg={isMobile ? '#000' : 'transparent'}
              hasBorder="2px solid #fff"
            >
              <Icon type="arrowRight" col="#fff" dimensions={14} />
            </Row>
          </Clickable>
        )}

        <Row
          borderBox
          column={isMobile}
          overflow={isMobile ? 'auto' : 'hidden'}
          hasRadius={isMobile ? '12px' : '24px'}
          wid={'90vw'}
          bg="#fff"
          // ht={!isMobile ? '600px' : isMobile && !isDeviceIOS() ? '80vh' : '600px'}
          ht={'80vh'}
          marg="0 auto"
          maxWid="936px"
          ref={postRef}
          start
        >
          {/* MEDIA */}
          <Col
            boxSizing="border-box"
            ht={!isMobile ? '100%' : isMobile && !isDeviceIOS() ? '80vh' : '300px'}
            minHt={'50vh'}
            pad={isMobile ? '12px 0' : 0}
            bg="#000"
            wid="100%"
            style={{ alignItems: isDeviceIOS() ? 'flex-start' : 'center' }}
          >
            {!!post?.media?.length && showImage && (
              <Images data={post?.media || []} isMobile={isMobile} ht={!isMobile ? '100%' : null} />
            )}
            {post?.youtubeId && <Youtube youtubeId={post?.youtubeId} />}

            {(post?.metaData?.image || post?.metaData?.icon) && (
              <Col marg="0 auto">
                <WebsiteView metaData={post?.metaData} />
              </Col>
            )}

            {post?.processing && (
              <Col center>
                <Text col="#fff" c1 bold>
                  {t('post?.beingProcessed')}
                </Text>
              </Col>
            )}
          </Col>

          {/* DETAILS / CAPTION */}
          <Col ht="100%" between wid="100%">
            <Header post={post} owner={owner} handleClose={handleClose} isFollowing={isFollowing} handleFollowClick={handleFollowClick} fromNavigation={fromNavigation} isSharedPost={isSharedPost} />

            <Col
              wid="calc(100% - 48px)"
              start
              pad="8px 24px"
              ht={isMobile && !isSharedPost ? '703px' : null}
              style={{ minHeight: 0, overflowY: 'auto' }}
            >
              <Caption post={post} handleClose={handleClose} />

              {!!comments?.length && (
                <Text b2 bold col={theme.GREY_70} marg="20px 0 12px">
                  {t('comments.commentCount', {
                    count: comments.length,
                    defaultValue: comments.length === 1 ? '1 comment' : `${comments.length} comments`
                  })}
                </Text>
              )}

              {threads.map((thread) => (
                <Comment key={thread._id} comment={thread} handleClose={handleClose} />
              ))}

              {!isSharedPost && fromNavigation && owner && owner._id !== user?._id && (
                <Row center pad="20px 0" marg="auto 0 0 0" wid="100%">
                  <Button
                    text={`View More from ${owner.name}`}
                    bg={theme.SECONDARY}
                    onClick={() => {
                      navigate(`/profile/${owner.username}`);
                      handleClose();
                    }}
                  />
                </Row>
              )}

              {isSharedPost && fromNavigation && owner && owner._id !== user?._id && (
                <Col
                  noFlex
                  start
                  pad="24px 20px"
                  gap="20px"
                  hasRadius="16px"
                  hasBorder={`1px solid ${theme.GREY_30}`}
                  boxSizing="border-box"
                  shadow
                  bg="#fff"
                  style={{
                    position: 'sticky',
                    bottom: '8px',
                    alignSelf: 'stretch',
                    marginTop: 'auto'
                  }}
                >
                  <Row noFlex start center gap="14px" wid="100%">
                    <Col
                      noFlex
                      centerAll
                      wid="48px"
                      minWid="48px"
                      ht="48px"
                      hasRadius="50%"
                      bg={theme.TALE}
                      hasBorder={`2px solid ${theme.GREY_10}`}
                      boxSizing="border-box"
                    >
                      <Text c1 bold>
                        {owner.name
                          ?.split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part[0]?.toUpperCase())
                          .join('')}
                      </Text>
                    </Col>

                    <Col noFlex start gap="4px" style={{ flex: 1, minWidth: 0 }}>
                      <Text b1 bold>
                        {owner.name}
                      </Text>

                      <Text b2 col={theme.GREY_60}>
                        Follow the team and explore more content after viewing this shared asset.
                      </Text>
                    </Col>
                  </Row>

                  <Col noFlex start gap="12px" wid="100%">
                    {!isFollowing && (
                      <Button
                        text="Follow Team"
                        big
                        bg={theme.SECONDARY}
                        onClick={handleFollowClick}
                      />
                    )}

                    <Button
                      text={`View More from ${owner.name}`}
                      big
                      bordered
                      style={{ border: `1px solid ${theme.GREY_30}` }}
                      onClick={() => {
                        navigate(`/profile/${owner.username}`);
                        handleClose();
                      }}
                    />
                  </Col>
                </Col>
              )}
            </Col>

            {/* Views Count */}
            <Row center start pad="12px 0 12px 24px" gap="10px">
              {isAPILoading ? (
                <Spinner2 />
              ) : (
                <Row gap="20px" center start>
                  {!isPostReacted && (
                    <Row center start gap="10px">
                      <Icon type="like" style={{ opacity: '.65' }} />
                      <Row gap="6px" center>
                        <Text weight="bold">Likes</Text>0
                      </Row>
                    </Row>
                  )}
                  <Row center start gap="10px">
                    <Icon type="visibleOn" style={{ opacity: '.65' }} />
                    <Row gap="6px" center>
                      <Text weight="bold">Views</Text>
                      {/* OR: get post views count from the local state instead of the props since it is 0, having only the owner's view is updated */}
                      {currentPostViewsCount || post?.viewsCount}
                    </Row>
                  </Row>
                </Row>
              )}
            </Row>

            {hasComments && isLoggedIn && (
              <Footer
                post={post}
                setComments={setComments}
                onHandleUpdateProfileViewsCount={onHandleUpdateProfileViewsCount}
                setIsAPILoading={setIsAPILoading}
                setCurrentPostViewsCount={setCurrentPostViewsCount}
              />
            )}
          </Col>
        </Row>
      </Col>

      {/* Call To Action Modal */}
      {owner && (
        <CallToActionModal
          isVisible={isCTAModalVisible}
          onCloseModal={() => setIsCTAModalVisible(false)}
          otherUser={owner}
          setOtherUser={() => { }}
          cta={'follow'}
          isFollowing={isFollowing}
          isSkipToSignUp={false}
          setShoutoutSelection={() => { }}
          isPaymentRequired={false}
          setPaymentModalVisible={() => { }}
          deeplinkPath={window.location.pathname}
        />
      )}
    </>
  );
};

export default Post;
